import { useState, useEffect, useCallback, useMemo } from "react";
import HttpClient, { Methods } from "../../../../services/HttpClient";
import { formatDisplayDate } from "../../../../util/dateFormat";
import { Button, Box, TextField, IconButton } from "@mui/material";
import { OverviewHeaderBar } from "../../../common/OverviewHeaderBar";
import { ScrollableDataGrid } from "../../../common/ScrollableDataGrid";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../constants/AppRoutes";
import { endpoints } from "../../../../constants/Endpoints";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import ArchiveIcon from "@mui/icons-material/Archive";

// Icons
import RefreshIcon from "@mui/icons-material/Refresh";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

export function LeadTable(props) {
  const refreshData = () => {
    setLoading(true);
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
        setSelectionModel([]);
      }
    });
  };

  const styles = {
    exportToCsvButton: {
      backgroundColor: "#595959",
      color: "#ffffff",
      "&:hover": {
        color: "#d01717",
      },
    },
    exportToCsvLink: {
      color: "white",
      textDecoration: "none",
    },
    newButton: {
      backgroundColor: "#eb008b",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    refreshButton: {
      backgroundColor: "#f68121",
      color: "#ffffff",
      "&:hover": {
        color: "#eb008b",
      },
    },
    mailButton: {
      marginLeft: "20px",
      backgroundColor: "#ec2027",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "yellow !important",
      },
    },
  };

  const sendAccountActivationEmail = async (id) => {
    setLoading(true);

    const response = await HttpClient.doRequest(
      Methods.POST,
      endpoints.lead.sendRentalPriceCalculationActivationMail + id
    );

    const message = await response.json();

    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      Toast.fire({
        icon: message.icon,
        title: message.title,
      });
    } else {
      Swal.fire({
        icon: message.icon,
        title: message.title,
        text: message.content,
        confirmButtonColor: "#888",
      });
    }
    setLoading(false);
  };

  const getData = useCallback(async () => {
    const response = await HttpClient.doRequest(
      Methods.GET,
      endpoints.lead.getRentalPriceCalculations,
      null
    );
    return await response.json();
  }, []);

  const archiveLead = async (id) => {
    setLoading(true);
    const response = await HttpClient.doRequest(
      Methods.DELETE,
      endpoints.lead.removeRentalPriceCalculation + id,
      null
    );
    if (response.ok) {
      const Toast = Swal.mixin({
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2500,
        timerProgressBar: true,
      });
      Toast.fire({ icon: "success", title: "Aanvraag gearchiveerd" });
    } else {
      const msg = await response.json().catch(() => null);
      Swal.fire({
        icon: "error",
        title: "Archiveren mislukt",
        text: msg?.content || "Kan aanvraag niet archiveren.",
        confirmButtonColor: "#888",
      });
    }
    const refreshed = await getData();
    setData(refreshed);
    setLoading(false);
  };

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [isMounted, setMounted] = useState(true);
  const navigate = useNavigate();
  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: "Acties",
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 0.5,
            }}
          >
            <IconButton
              size="small"
              title="Stuur bevestigings mail"
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: "",
                  text: `E-mail versturen naar ${params.row.leadApplicationUserEmail}?`,
                  icon: "info",
                  showCancelButton: true,
                  confirmButtonColor: "#eb008b",
                  confirmButtonText: "Versturen",
                }).then((result) => {
                  if (result.value) {
                    sendAccountActivationEmail(params.row.id);
                  }
                });
              }}
            >
              <MailOutlineIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              title="Archiveer aanvraag"
              onClick={(e) => {
                e.stopPropagation();
                Swal.fire({
                  title: "Aanvraag archiveren",
                  text: "Weet je zeker dat je deze huurprijsaanvraag wilt archiveren?",
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonColor: "#ec2027",
                  confirmButtonText: "Archiveer",
                  cancelButtonText: "Annuleren",
                }).then((res) => {
                  if (res.isConfirmed) {
                    archiveLead(params.row.id);
                  }
                });
              }}
            >
              <ArchiveIcon fontSize="inherit" />
            </IconButton>
          </Box>
        ),
      },
      { field: "street", headerName: "Straat", flex: 1, minWidth: 160 },
      { field: "number", headerName: "Nummer", width: 90 },
      {
        field: "leadApplicationUserSalutation",
        headerName: "Aanhef",
        width: 120,
      },
      {
        field: "leadApplicationUserFirstName",
        headerName: "Voornaam",
        width: 150,
      },
      {
        field: "leadApplicationUserLastName",
        headerName: "Achternaam",
        width: 170,
      },
      {
        field: "leadApplicationUserEmail",
        headerName: "E-mail",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "leadApplicationUserPhoneNumber",
        headerName: "Telefoonnummer",
        width: 160,
      },
      {
        field: "leadRegistrationDate",
        headerName: "Datum aanvraag",
        width: 160,
        renderCell: (p) => {
          const value = p?.row?.leadRegistrationDate;
          if (!value) return <span>-</span>;
          let formatted = formatDisplayDate(value) || "-";
          return <span>{formatted}</span>;
        },
      },
    ],
    []
  );

  const csvHeaders = [
    { label: "Datum aanvraag", key: "leadRegistrationDate" },
    { label: "Aanvraag bevestigd", key: "leadIsConfirmed" },
    { label: "Aanhef", key: "leadApplicationUserSalutation" },
    { label: "Achternaam", key: "leadApplicationUserLastName" },
    { label: "Voornaam", key: "leadApplicationUserFirstName" },
    { label: "Telefoonnummer", key: "leadApplicationUserPhoneNumber" },
    { label: "Email", key: "leadApplicationUserEmail" },
    { label: "Is eigenaar", key: "isOwner" },
    { label: "Aantal slaapkamers", key: "numberOfBedrooms" },
    { label: "Aantal m2", key: "surfaceInSquareMeters" },
    { label: "Oplevering", key: "interior" },
    { label: "Straat", key: "street" },
    { label: "Huisnummer", key: "houseNumber" },
    { label: "Toevoeging", key: "addition" },
    { label: "Postcode", key: "zipCode" },
    { label: "Stad", key: "city" },
    { label: "Extra info", key: "extraInfo" },
    { label: "Voorgestelde huurprijs", key: "proposedRentalPrice" },
    { label: "Voorgesteld op", key: "dateRentalPriceProposedOn" },
    { label: "Eigen notities", key: "SHBNotes" },
  ];

  useEffect(() => {
    getData().then((v) => {
      if (isMounted) {
        setData(v);
        setLoading(false);
      }
    });

    return () => {
      setMounted(false);
    };
  }, [isMounted, getData]);

  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => {
    if (!search) return data;
    const s = search.toLowerCase();
    return data.filter(
      (r) =>
        r.leadApplicationUserFirstName?.toLowerCase().includes(s) ||
        r.leadApplicationUserLastName?.toLowerCase().includes(s) ||
        r.leadApplicationUserEmail?.toLowerCase().includes(s) ||
        r.street?.toLowerCase().includes(s)
    );
  }, [data, search]);

  const onRowClick = (params) => {
    navigate(appRoutes.shb.lead.rentalPriceCalculationDetail + params.row.id);
  };

  return (
    <Box>
      <OverviewHeaderBar
        title="Huurprijsaanvragen"
        actions={
          <>
            <TextField
              size="small"
              label="Zoeken"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <CSVLink
              data={data}
              style={styles.exportToCsvLink}
              headers={csvHeaders}
              filename="huurprijsaanvragen.csv"
            >
              <Button
                variant="contained"
                startIcon={<SystemUpdateAltIcon />}
                style={styles.exportToCsvButton}
              >
                Download CSV
              </Button>
            </CSVLink>
            <Button
              variant="contained"
              style={styles.refreshButton}
              startIcon={<RefreshIcon />}
              onClick={refreshData}
            >
              <span>Vernieuw</span>
            </Button>
          </>
        }
      />
      <Box sx={{ mx: 3, mb: 3 }}>
        <ScrollableDataGrid
          rows={(Array.isArray(filteredRows) ? filteredRows : []).filter(
            (r) => r && r.id !== null && r.id !== undefined
          )}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r.id}
          disableRowSelectionOnClick
          onRowClick={onRowClick}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          dataGridSx={{ backgroundColor: "background.paper" }}
        />
      </Box>
    </Box>
  );
}
